import { PackageTargetUpgradeDTO, PackageUpgradeTableRow } from '~/services/OffersService';

import getPackagePriceByCurrency from './getPackagePriceByCurrency';

export interface PackageUpgradeMergeOptions {
  mapUpgradeType: (apiUpgradeType: string) => PackageUpgradeTableRow['upgradeType'];
  mapMappingSource: (mappingSource: string) => PackageUpgradeTableRow['manualOrDefault'];
  packageById: Record<string, App.Package>;
}

export function mergePackageUpgrades(
  defaultRows: PackageUpgradeTableRow[],
  existingUpgrades: PackageTargetUpgradeDTO[],
  options: PackageUpgradeMergeOptions,
): PackageUpgradeTableRow[] {
  // Map existing upgrades by base_package_id + upgrade_type
  const existingUpgradesMap = new Map<string, PackageTargetUpgradeDTO>();
  existingUpgrades.forEach((upgrade) => {
    const key = `${upgrade.base_package_id}-${upgrade.upgrade_type}`;
    existingUpgradesMap.set(key, upgrade);
  });

  return defaultRows.map((row) => {
    const upgradeTypeKey = row.upgradeType === 'Room type upgrade' ? 'room' : 'package';
    const key = `${row.basePackageId}-${upgradeTypeKey}`;
    const existingUpgrade = existingUpgradesMap.get(key);

    if (existingUpgrade) {
      const targetPackage = options.packageById[existingUpgrade.target_package_id];
      return {
        ...row,
        id: existingUpgrade.id,
        targetPackageName: targetPackage.deal_option_name,
        targetPackageId: existingUpgrade.target_package_id,
        upgradeType: options.mapUpgradeType(existingUpgrade.upgrade_type),
        manualOrDefault: options.mapMappingSource(existingUpgrade.mapping_source),
      };
    }

    return row;
  });
}

export type UpgradeType = 'room' | 'package';

export function filterPackageOptions(
  basePackage: App.Package,
  candidatePackage: App.Package,
  upgradeType: UpgradeType,
): boolean {
  // Exclude the base package itself
  if (candidatePackage.le_package_id === basePackage.le_package_id) return false;
  // Check duration conditions - packages must have the same duration
  // for accommodation packages we consider the number of nights
  if ('number_of_nights' in basePackage && 'number_of_nights' in candidatePackage) {
    if (basePackage.number_of_nights !== candidatePackage.number_of_nights) return false;
  }

  // Check room type conditions
  if ('fk_room_type_id' in basePackage && 'fk_room_type_id' in candidatePackage) {
    if (upgradeType === 'room') {
      return basePackage.fk_room_type_id !== candidatePackage.fk_room_type_id;
    }

    if (upgradeType === 'package') {
      return basePackage.fk_room_type_id === candidatePackage.fk_room_type_id;
    }
  }

  return true;
}

export function findUpgradePackageOptions(
  basePackage: App.Package,
  packages: App.Package[],
  upgradeType: UpgradeType,
): App.Package[] {
  if (!basePackage) return [];

  return (packages || []).filter((pkg) => filterPackageOptions(basePackage, pkg, upgradeType));
}

export interface PackageCostCalculationParams {
  basePackage: App.Package;
  targetPackage: App.Package;
  experienceCosts: Map<string, number>;
}

export interface IncrementalCostResult {
  percentage: number;
  absoluteValue: number;
}

export function calculateIncrementalPackageCost({
  basePackage,
  targetPackage,
  experienceCosts,
}: PackageCostCalculationParams): IncrementalCostResult {
  // Get AUD prices directly (no conversion needed)
  const basePackagePricesInAUD = getPackagePriceByCurrency(basePackage, 'AUD');
  const targetPackagePricesInAUD = getPackagePriceByCurrency(targetPackage, 'AUD');

  // Add null checks to prevent undefined errors
  if (!basePackagePricesInAUD || !targetPackagePricesInAUD) {
    console.warn('Missing AUD pricing data for packages', {
      basePackageId: basePackage.le_package_id,
      targetPackageId: targetPackage.le_package_id,
      basePackagePricesInAUD: !!basePackagePricesInAUD,
      targetPackagePricesInAUD: !!targetPackagePricesInAUD,
    });
    return { percentage: 0, absoluteValue: 0 };
  }

  // Use id_salesforce_external for experience costs
  const baseExperienceCost = experienceCosts.get(basePackage.id_salesforce_external) || 0;
  const targetExperienceCost = experienceCosts.get(targetPackage.id_salesforce_external) || 0;

  const baseCostPrice = (basePackagePricesInAUD.cost_aud || 0) + baseExperienceCost;
  const targetCostPrice = (targetPackagePricesInAUD.cost_aud || 0) + targetExperienceCost;

  const baseAudPrice = basePackagePricesInAUD.price_aud || 0;
  const adjustedMarginBase = baseAudPrice - baseCostPrice;

  if (adjustedMarginBase !== 0) {
    const absoluteValue = targetCostPrice - baseCostPrice;
    const incrementalCost = (targetCostPrice - baseCostPrice) / adjustedMarginBase;
    return {
      percentage: Math.round(incrementalCost * 100) / 100,
      absoluteValue,
    };
  }

  return { percentage: 0, absoluteValue: 0 };
}
